<template>
    <AppLayout>
        <div class="bg-white">
            <div class="pt-6">
                <nav aria-label="Breadcrumb">
                    <ol role="list" class="mx-auto flex max-w-2xl items-center space-x-2 px-4 sm:px-6 lg:max-w-7xl lg:px-8">
                        <li v-for="breadcrumb in page.breadcrumbs" :key="breadcrumb.id">
                            <div class="flex items-center">
                                <a :href="breadcrumb.href" class="mr-2 text-sm font-medium text-gray-900">{{ breadcrumb.name
                                    }}</a>
                                <svg width="16" height="20" viewBox="0 0 16 20" fill="currentColor" aria-hidden="true"
                                    class="h-5 w-4 text-gray-300">
                                    <path d="M5.697 4.34L8.98 16.532h1.327L7.025 4.341H5.697z" />
                                </svg>
                            </div>
                        </li>
                        <li class="text-sm">
                            <a href="#" aria-current="page" class="font-medium text-gray-500 hover:text-gray-600">{{
                                page.name }}</a>
                        </li>
                    </ol>
                </nav>
    
                <!-- page info -->
                <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <form class="mt-10 space-y-8">
                        <div class="space-y-12">
                            <div class="border-b border-[#E5E5E5] pb-12">
                                <h2 class="text-xs font-semibold uppercase tracking-widest text-[#A07830]">Account</h2>
    
                                <div class="mt-10 sm:mx-auto sm:w-full">
                                    <form v-if="user" class="space-y-6" action="#" method="POST" @submit.prevent="submitForm">
                                        <div v-if="errors.general" class="mb-4 text-sm text-red-600">
                                            {{ errors.general }}
                                        </div>
                                        <div>
                                            <div class="mt-2">
                                                <FloatLabel variant="in" class="w-full">
                                                    <InputText id="firstName" v-model="user.firstName" variant="filled"
                                                        class="w-full" :invalid="!!errors.firstName" type="text"
                                                        @focus="errors.firstName = null" />
                                                    <label for="firstName">First Name</label>
                                                </FloatLabel>
                                                <div v-if="errors.firstName" class="mt-1 text-sm text-red-600">
                                                    {{ errors.firstName }}
                                                </div>
                                            </div>
                                        </div>
    
                                        <div>
                                            <div class="mt-2">
                                                <FloatLabel variant="in" class="w-full">
                                                    <InputText id="lastName" v-model="user.lastName" variant="filled"
                                                        class="w-full" :invalid="!!errors.lastName" type="text"
                                                        @focus="errors.lastName = null" />
                                                    <label for="lastName">Last Name</label>
                                                </FloatLabel>
                                                <div v-if="errors.lastName" class="mt-1 text-sm text-red-600">
                                                    {{ errors.lastName }}
                                                </div>
                                            </div>
                                        </div>
    
                                        <div>
                                            <div class="mt-2">
                                                <FloatLabel variant="in" class="w-full">
                                                    <Select id="gender" v-model="user.gender" variant="filled"
                                                        class="w-full" option-label="label" option-value="value"
                                                        :options="genderOptions" :highlightOnSelect="true"
                                                        :invalid="!!errors.gender" @focus="errors.gender = null">
                                                        <template #option="{ option }">
                                                            <span>{{ option?.label || 'Select Gender' }}</span>
                                                        </template>
                                                    </Select>
                                                    <label for="gender">Gender</label>
                                                </FloatLabel>
                                                <div v-if="errors.gender" class="mt-1 text-sm text-red-600">
                                                    {{ errors.gender }}
                                                </div>
                                            </div>
                                        </div>
    
                                        <div>
                                            <div class="mt-2">
                                                <FloatLabel variant="in" class="w-full">
                                                    <InputText id="phone" v-model="user.phone" variant="filled"
                                                        class="w-full" type="tel" :invalid="!!errors.phone"
                                                        @focus="errors.phone = null" />
                                                    <label for="phone">Phone</label>
                                                </FloatLabel>
                                                <div v-if="errors.phone" class="mt-1 text-sm text-red-600">
                                                    {{ errors.phone }}
                                                </div>
                                            </div>
                                        </div>
    
                                        <div>
                                            <div class="mt-2">
                                                <FloatLabel variant="in" class="w-full">
                                                    <InputText id="email" v-model="user.email" variant="filled"
                                                        class="w-full" type="email" :invalid="!!errors.email"
                                                        @focus="errors.email = null" />
                                                    <label for="email">Email</label>
                                                </FloatLabel>
                                                <div v-if="errors.email" class="mt-1 text-sm text-red-600">
                                                    {{ errors.email }}
                                                </div>
                                            </div>
                                        </div>
    
                                        <div>
                                            <div class="mt-2">
                                                <FloatLabel variant="in" class="w-full">
                                                    <InputText id="password" v-model="user.password" variant="filled"
                                                        class="w-full" type="password" :invalid="!!errors.password"
                                                        @focus="errors.password = null" />
                                                    <label for="password">Password</label>
                                                </FloatLabel>
                                                <div v-if="errors.password" class="mt-1 text-sm text-red-600">
                                                    {{ errors.password }}
                                                </div>
                                            </div>
                                        </div>
    
                                        <div>
                                            <Button class="w-full" type="submit" :loading="loading"
                                                label="Save Changes"
                                                style="background: #17364a; border: none; border-radius: 0; font-weight: 600;">
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </AppLayout>
</template>

<script setup>
import AppLayout from '@/components/layouts/AppLayout.vue';
import { ref } from 'vue';
import InputText from 'primevue/inputtext';
import FloatLabel from 'primevue/floatlabel';
import Button from 'primevue/button';
import Select from 'primevue/select';
import { useAuth } from '@/composables/useAuth';
import { watch } from 'vue';

const { user: authUser } = useAuth();
const errors = ref({});
const user = ref({});

const genderOptions = ref([
    { label: 'Select Gender', value: null, disabled: true },
    { label: 'Male', value: 1 },
    { label: 'Female', value: 2 },
    { label: 'Prefer not to say', value: 0 }
]);

watch(authUser, (u) => {
    if (u) {
        user.value = {
            ...u,
            gender: genderOptions.value.find(option => option.value === u.gender)?.value ?? genderOptions.value.find(option => option.value === 0)?.value,
        };
    }
}, { immediate: true });

const page = {
    name: 'Account',
    breadcrumbs: [
        { id: 1, name: 'Home', href: '/' },
    ],
};

const loading = ref(false);

const submitForm = () => {
    loading.value = true;
    axios.post('/update-user', user.value, {
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        }
    })
        .then(response => {
            console.log('User updated successfully:', response.data);
        })
        .catch(error => {
            // Handle validation errors
            Object.keys(errors.value).forEach(key => {
                errors.value[key] = null;
            });
            if (error.response && error.response.status === 422) {
                const validationErrors = error.response.data.errors;
                for (const key in validationErrors) {
                    if (Object.prototype.hasOwnProperty.call(validationErrors, key)) {
                        errors.value[key] = validationErrors[key][0];
                    }
                }
            } else {
                errors.value.general = 'An unexpected error occurred. Please try again later.';
                console.error('Registration failed:', error.response.data.message || error.message);
            }
        }).finally(() => {
            loading.value = false;
        });
};

</script>